/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Plus, Search, TrendingUp } from "lucide-react";
import { SelectDropdown, StatusBadge, TextInput } from "../../../../components/ui/index.js";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canManageAllProjects } from "../../../../lib/permissions.js";
import { CUSTOMER_STATE } from "../../../../components/catalog/catalogUtils.js";
import { ContactActions, CrmListCard, CrmListSkeleton } from "../../../../components/crm/CrmParts.jsx";
import { LEAD_SOURCES, LEAD_STAGES, labelFor, leadName, optionFor } from "../../../../components/crm/crmUtils.js";
import useSalesOptions from "../../../../components/crm/useSalesOptions.js";
import LeadFicha from "./LeadFicha.jsx";

const STAGE_FILTERS = [{ value: "", label: "Todos" }, ...LEAD_STAGES];
const SOURCE_FILTERS = [{ value: "", label: "Todas las fuentes" }, ...LEAD_SOURCES];

/**
 * Admin › CRM › Leads (C3D-46). La ficha va en `?id=<id>` y el alta en
 * `?nuevo=1`, sin salir de la sección del panel.
 */
export default function AdminLeads() {
    const seesAll = canManageAllProjects(useAuthContext());
    const [searchParams, setSearchParams] = useSearchParams();
    const openId = Number(searchParams.get("id")) || null;
    const creating = searchParams.get("nuevo") === "1";

    const [search, setSearch] = useState("");
    const [stage, setStage] = useState("");
    const [source, setSource] = useState("");
    const [assignedTo, setAssignedTo] = useState("");
    const [state, setState] = useState({ loading: true, error: false, items: [], next: null, count: 0 });
    const [page, setPage] = useState(1);
    const salesOptions = useSalesOptions(seesAll);

    const load = useCallback((pageToLoad = 1) => {
        setState((prev) => ({ ...prev, loading: true, error: false }));
        axios.get("/leads/", {
            params: {
                search: search || undefined,
                stage: stage || undefined,
                source: source || undefined,
                assigned_to: assignedTo || undefined,
                page: pageToLoad,
            },
        })
            .then(({ data }) => {
                const results = data.results ?? data;
                setState((prev) => ({
                    loading: false,
                    error: false,
                    items: pageToLoad === 1 ? results : [...prev.items, ...results],
                    next: data.next ?? null,
                    count: data.count ?? results.length,
                }));
            })
            .catch(() => setState((prev) => ({ ...prev, loading: false, error: true })));
        setPage(pageToLoad);
    }, [search, stage, source, assignedTo]);

    useEffect(() => {
        if (openId || creating) return undefined;
        const timer = setTimeout(() => load(1), 250);
        return () => clearTimeout(timer);
    }, [load, openId, creating]);

    const close = () => setSearchParams({});

    if (openId || creating) {
        return (
            <LeadFicha
                key={openId || "nuevo"}
                leadId={openId}
                onBack={close}
                onSaved={(id) => setSearchParams({ id: String(id) })}
            />
        );
    }

    const filtering = Boolean(search || stage || source || assignedTo);

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-bold">Leads</h1>
                    <p className="text-sm text-base-content/70">
                        {state.count} {state.count === 1 ? "oportunidad" : "oportunidades"}{seesAll ? "" : " que llevas tú"}
                    </p>
                </div>
                <button type="button" className="btn btn-primary btn-sm gap-2" onClick={() => setSearchParams({ nuevo: "1" })}>
                    <Plus className="w-4 h-4" aria-hidden="true" /> Nuevo lead
                </button>
            </header>

            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                    <TextInput
                        leftIcon={<Search className="w-4 h-4" />}
                        placeholder="Empresa, contacto, email, teléfono o web"
                        aria-label="Buscar leads"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        inputSize="sm"
                        className="md:max-w-sm"
                    />
                    <SelectDropdown options={SOURCE_FILTERS} value={source} onChange={setSource} size="sm" className="md:w-52" />
                    {seesAll && salesOptions.length > 0 && (
                        <SelectDropdown
                            options={[{ value: "", label: "Todos los comerciales" }, ...salesOptions]}
                            value={assignedTo}
                            onChange={setAssignedTo}
                            size="sm"
                            className="md:w-56"
                        />
                    )}
                </div>
                <div role="group" aria-label="Filtrar por etapa" className="flex gap-2 overflow-x-auto pb-1">
                    {STAGE_FILTERS.map((option) => (
                        <button
                            key={option.value || "all"}
                            type="button"
                            aria-pressed={stage === option.value}
                            onClick={() => setStage(option.value)}
                            className={`btn btn-sm rounded-full shrink-0 ${stage === option.value ? "btn-neutral" : "btn-ghost border border-base-content/15"}`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {state.error && (
                <div role="alert" className="alert alert-error justify-between">
                    <span>No se han podido cargar los leads.</span>
                    <button type="button" className="btn btn-sm" onClick={() => load(1)}>Reintentar</button>
                </div>
            )}

            {state.loading && state.items.length === 0 && <CrmListSkeleton />}

            {!state.loading && !state.error && state.items.length === 0 && (
                <div className="flex flex-col items-center text-center gap-3 py-16 rounded-xl border border-dashed border-base-content/20">
                    <TrendingUp className="w-10 h-10 text-base-content/40" aria-hidden="true" />
                    <p className="font-semibold">{filtering ? "Ningún lead con estos filtros" : "Todavía no hay leads"}</p>
                    <p className="text-sm text-base-content/70 max-w-sm">Da de alta los negocios que encuentres y sigue aquí cómo avanza cada uno.</p>
                </div>
            )}

            {state.items.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {state.items.map((lead) => {
                        const stageOption = optionFor(LEAD_STAGES, lead.stage);
                        const customer = CUSTOMER_STATE[lead.customer_state];
                        const meta = [
                            labelFor(LEAD_SOURCES, lead.source),
                            lead.assigned_to_name && `Comercial: ${lead.assigned_to_name}`,
                            lead.project_count > 0 && `${lead.project_count} ${lead.project_count === 1 ? "proyecto" : "proyectos"}`,
                        ].filter(Boolean).join(". ");
                        return (
                            <CrmListCard
                                key={lead.id}
                                title={leadName(lead)}
                                subtitle={lead.company_name && lead.contact_name ? lead.contact_name : lead.email || lead.phone}
                                imageUrl={lead.logo_asset}
                                onOpen={() => setSearchParams({ id: String(lead.id) })}
                                badges={(
                                    <>
                                        {stageOption && <StatusBadge label={stageOption.label} tone={stageOption.tone} size="xs" />}
                                        {lead.customer_state === "cliente" && customer && (
                                            <StatusBadge label="Ha comprado" tone="success" title={customer.title} size="xs" outline />
                                        )}
                                    </>
                                )}
                                meta={meta}
                                actions={<ContactActions phone={lead.phone} email={lead.email} size="xs" />}
                            />
                        );
                    })}
                </div>
            )}

            {state.next && (
                <button type="button" className="btn btn-ghost btn-sm self-center" onClick={() => load(page + 1)} disabled={state.loading}>
                    {state.loading && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                    Cargar más
                </button>
            )}
        </div>
    );
}
